import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import {
  formatCurrency,
  formatCurrencyDecimals,
  formatCurrencyDecimalsLong,
  formatNumber,
  formatNumberDecimalsLong,
} from 'utils/formats';

import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';

import { distributeSellUnitsByPlan } from '../utils/distributeSellUnitsByPlan';

type IncentiveOfferSellBreakdownProps = {
  sellerPlans: IncentivesMarketPlanContext[];
  purchaseQuantity: number;
  unitPrice: number;
  retentionPercent?: number;
};

const IncentiveOfferSellBreakdown: FC<IncentiveOfferSellBreakdownProps> = ({
  sellerPlans,
  purchaseQuantity,
  unitPrice,
  retentionPercent = 0,
}) => {
  const { t } = useTranslate();

  const { rows, total, totalRemaining } = useMemo(
    () =>
      distributeSellUnitsByPlan({
        plans: sellerPlans,
        totalUnits: purchaseQuantity,
        sellingPrice: unitPrice,
        retentionPercent,
      }),
    [sellerPlans, purchaseQuantity, unitPrice, retentionPercent]
  );

  if (!sellerPlans.length) {
    return null;
  }

  return (
    <div>
      <h6 className="overline-title text-soft mb-3">
        {t('IncentivesMarketSellBreakdownTitle')}
      </h6>

      {rows.map((row) => {
        const rowKey = row.planId || row.beneficiaryId;

        return (
          <div
            key={rowKey}
            className={`border rounded p-2 mb-2${
              row.unitsSold === 0 ? ' opacity-50' : ''
            }`}
          >
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="fw-medium text-truncate pr-2">
                {row.planName}
              </span>
              <div className="text-right flex-shrink-0">
                <span className="badge badge-dim badge-primary">
                  {formatNumber(row.unitsSold)}{' '}
                  {t('IncentivesMarketUnits').toLowerCase()}
                </span>
                <span className="d-block text-soft fs-10px mt-1">
                  {t('Available')}: {formatNumber(row.availableUnits)}
                </span>
              </div>
            </div>

            <div className="row gx-1 fs-12px">
              <div className="col-3">
                <span className="text-soft d-block fs-10px">
                  {t('StrikePriceUnit')}
                </span>
                {formatCurrencyDecimalsLong(row.sharePrice)}
              </div>
              <div className="col-3">
                <span className="text-soft d-block fs-10px">
                  {t('SellingPriceUnit')}
                </span>
                {formatCurrencyDecimalsLong(row.sellingPrice)}
              </div>
              <div className="col-3">
                <span className="text-soft d-block fs-10px">
                  {t('PercentRetentionColumn')}
                </span>
                {formatNumberDecimalsLong(row.retentionPercent)}%
              </div>
              <div className="col-3 text-right">
                <span className="text-soft d-block fs-10px">{t('Profit')}</span>
                <strong>{formatCurrencyDecimals(row.result)}</strong>
              </div>
            </div>
          </div>
        );
      })}

      <div className="d-flex justify-content-between py-3 border-top">
        <span className="fs-16px">{t('TotalToPay')}</span>
        <strong className="fs-20px text-info">{formatCurrency(total)}</strong>
      </div>

      <div className="alert alert-light mb-0">
        <h6 className="alert-heading fs-13px mb-2">{t('AfterPurchase')}:</h6>
        {rows.map((row) => {
          const rowKey = `after-${row.planId || row.beneficiaryId}`;

          return (
            <div
              key={rowKey}
              className="d-flex justify-content-between align-items-center fs-13px mb-1"
            >
              <span className="text-truncate pr-2">{row.planName}</span>
              <strong className="flex-shrink-0">
                {formatNumber(row.remainingUnits)} {t('Remaining').toLowerCase()}
              </strong>
            </div>
          );
        })}
        <div className="d-flex justify-content-between align-items-center fs-13px pt-2 border-top mt-2">
          <span className="text-soft">{t('Total')}</span>
          <strong>
            {formatNumber(totalRemaining)}{' '}
            {t('IncentivesMarketUnits').toLowerCase()}
          </strong>
        </div>
      </div>
    </div>
  );
};

export default IncentiveOfferSellBreakdown;
